import React from 'react';
import { t } from '@lingui/macro';
import { Button, ButtonProps, useDisclosure } from '@cardboard-ui/react';
import { graphql, useFragment } from 'utils/graphClient';
import { CreateAnnouncementOverlay } from 'apps/TenantApp/screens/Space/screens/Activity/components/Announcements/components/CreateAnnouncementOverlay';
import { AnnouncementButton_node$key } from './__generated__/AnnouncementButton_node.graphql';
import { BullhornIcon } from 'components/icons';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';

const nodeFragment = graphql`
  fragment AnnouncementButton_node on Node {
    __typename
    id
    ... on Folder {
      publishState
      announcementSpace: space {
        __typename
        id
        modules {
          ... on SpaceAnnouncementsModule {
            canCreateAnnouncement {
              value
            }
          }
        }
      }
    }
    ... on FolderItem {
      publishState
      announcementSpace: space {
        __typename
        id
        modules {
          ... on SpaceAnnouncementsModule {
            canCreateAnnouncement {
              value
            }
          }
        }
      }
    }
    ... on Event {
      publishState
      announcementSpace: space {
        __typename
        id
        modules {
          ... on SpaceAnnouncementsModule {
            canCreateAnnouncement {
              value
            }
          }
        }
      }
    }
  }
`;

interface AnnouncementButtonProps extends ButtonProps {
  node: AnnouncementButton_node$key;
  closeMenu?: () => void;
}

export const AnnouncementButton = ({
  node: _node,
  closeMenu,
  ...props
}: AnnouncementButtonProps) => {
  const activeLayout = useActiveLayout();
  const isDesktop = activeLayout === Layout.DESKTOP;
  const node = useFragment(nodeFragment, _node);
  const {
    isOpen: isOpenOverlay,
    onOpen: onOpenOverlay,
    onClose: onCloseOverlay,
  } = useDisclosure();
  const space = node.announcementSpace;
  const canCreateAnnouncement =
    space?.modules.find((m) => !!m.canCreateAnnouncement?.value) &&
    node.publishState === 'PUBLISHED';

  if (!space || !canCreateAnnouncement) return null;

  const onOpen = () => {
    onOpenOverlay();
    if (isDesktop) {
      closeMenu && closeMenu();
    }
  };
  const onClose = () => {
    onCloseOverlay();
    if (!isDesktop) {
      closeMenu && closeMenu();
    }
  };

  return (
    <>
      <Button
        w="100%"
        size="sm"
        rightIcon={<BullhornIcon />}
        justifyContent="space-between"
        onClick={() => {
          onOpen();
        }}
        {...props}
      >
        {t`Create announcement`}
      </Button>

      <CreateAnnouncementOverlay
        spaceId={space.id}
        addToConnectionIds={[]}
        linkIds={[node.id]}
        isOpen={isOpenOverlay}
        onClose={onClose}
      />
    </>
  );
};
